/**
 * VideoLibraryPage — browse + delete the org's uploaded MP4s.
 *
 * Sits on top of the new `/api/v1/videos/library` endpoints:
 *   - GET    /videos/library                  — paginated list + ref rollup
 *   - DELETE /videos/library/{file_id}        — refuses with 409 if referenced
 *
 * UX choices:
 *   - Search box (filename contains, server-side, 300ms debounce).
 *   - Sort dropdown (newest / oldest / largest / smallest).
 *   - "Used in N module(s)" badge per row — clicking opens a popover with
 *     the references_preview list so trainers can navigate to detach.
 *   - Delete blocks behind a confirm dialog. On 409, the dialog mutates to
 *     show the full references list — trainer cannot force-delete (would
 *     orphan slide media_urls; safer to make them detach explicitly first).
 *   - Server-side pagination via offset; "Load more" button instead of
 *     infinite scroll to keep the page predictable on small mobile screens.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Film, Search, Trash2, ExternalLink, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../hooks/useApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { formatBytes } from '../utils/uploadVideoDirect';

const PAGE_SIZE = 20;

/** Format an ISO datetime for the "Uploaded" column.
 *  Falls back gracefully if the backend serialised something funky. */
function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  // "May 27, 2026 · 2:13 PM" — long enough to disambiguate same-day uploads,
  // short enough to fit in a table cell.
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

export default function VideoLibraryPage() {
  const api = useApi();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('newest');
  const [offset, setOffset] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);     // {file_id, filename} or null
  const [deleteBlocked, setDeleteBlocked] = useState(null);   // {reference_count, references}
  const [deleting, setDeleting] = useState(false);

  // Debounced server-side search. We track `q` immediately (controlled input)
  // but only requery 300ms after the user stops typing. Avoids hammering the
  // backend with one request per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => {
      setOffset(0);
      load({ q, sort, offset: 0, replace: true });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, sort]);

  const load = useCallback(async ({ q: qArg, sort: sortArg, offset: offArg, replace }) => {
    const setBusy = replace ? setLoading : setLoadingMore;
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (qArg) params.set('q', qArg);
      if (sortArg) params.set('sort', sortArg);
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(offArg || 0));
      const res = await api.get(`/videos/library?${params.toString()}`);
      setTotal(res.total || 0);
      setHasMore(!!res.has_more);
      setItems((prev) => (replace ? (res.items || []) : [...prev, ...(res.items || [])]));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Failed to load library');
    } finally {
      setBusy(false);
    }
  }, [api]);

  const handleLoadMore = async () => {
    const next = offset + PAGE_SIZE;
    setOffset(next);
    await load({ q, sort, offset: next, replace: false });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.del(`/videos/library/${deleteTarget.file_id}`);
      toast.success(`Deleted "${deleteTarget.filename}"`);
      // Optimistic removal — avoids a full refetch which would jump scroll.
      setItems((prev) => prev.filter((i) => i.file_id !== deleteTarget.file_id));
      setTotal((t) => Math.max(0, t - 1));
      setDeleteTarget(null);
      setDeleteBlocked(null);
    } catch (e) {
      // 409 with structured body → show the references inside the same modal.
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 409 && detail && typeof detail === 'object') {
        setDeleteBlocked({
          reference_count: detail.reference_count || 0,
          references: detail.references || [],
        });
      } else {
        toast.error(detail?.message || detail || e.message || 'Delete failed');
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="video-library-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
            <Film className="w-7 h-7 text-[var(--lumyn-content)]" />
            Video Library
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            All MP4s uploaded by your team. Search, sort, and delete — files in use can't be deleted until they're detached from their slides.
          </p>
        </div>
        <Button asChild variant="outline" data-testid="video-library-upload-link">
          <Link to="/editor/modules/upload">Upload new video</Link>
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search filenames…"
            className="pl-8"
            data-testid="video-library-search"
          />
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-[180px]" data-testid="video-library-sort">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest first</SelectItem>
            <SelectItem value="oldest">Oldest first</SelectItem>
            <SelectItem value="largest">Largest first</SelectItem>
            <SelectItem value="smallest">Smallest first</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground ml-auto tabular-nums" data-testid="video-library-count">
          {loading ? '…' : `${total} video${total === 1 ? '' : 's'}`}
        </p>
      </div>

      {/* Table */}
      <div className="rounded-md border bg-card overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-2.5 border-b bg-muted/30 text-xs font-medium text-muted-foreground uppercase tracking-wide">
          <div className="col-span-5">File</div>
          <div className="col-span-2">Size</div>
          <div className="col-span-2">Uploaded</div>
          <div className="col-span-2">Usage</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {loading ? (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Loading library…
          </div>
        ) : items.length === 0 ? (
          <div className="px-4 py-16 text-center" data-testid="video-library-empty">
            <Film className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm font-medium">No videos yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              {q ? 'No files match your search.' : 'Upload your first MP4 to see it here.'}
            </p>
          </div>
        ) : (
          items.map((it) => (
            <LibraryRow
              key={it.file_id}
              item={it}
              onRequestDelete={() => { setDeleteBlocked(null); setDeleteTarget({ file_id: it.file_id, filename: it.filename }); }}
            />
          ))
        )}
      </div>

      {hasMore && !loading && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={handleLoadMore}
            disabled={loadingMore}
            data-testid="video-library-load-more"
          >
            {loadingMore ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Load more
          </Button>
        </div>
      )}

      {/* Delete dialog — mutates to a "references blocker" view if the server
          returns 409. Either state closes via the Cancel button. */}
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(o) => { if (!o && !deleting) { setDeleteTarget(null); setDeleteBlocked(null); } }}
      >
        <DialogContent data-testid="video-library-delete-dialog">
          {deleteBlocked ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Info className="w-5 h-5 text-amber-500" />
                  Can't delete — still in use
                </DialogTitle>
                <DialogDescription>
                  This video is referenced by {deleteBlocked.reference_count} slide{deleteBlocked.reference_count === 1 ? '' : 's'}.
                  Detach it from these modules first, then try again.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-1.5 max-h-80 overflow-y-auto">
                {deleteBlocked.references.map((r) => (
                  <Link
                    key={r.slide_id}
                    to={`/editor/modules/${r.module_id}`}
                    className="flex items-start justify-between gap-3 rounded-md border px-3 py-2 hover:bg-muted/50 transition-colors"
                    data-testid="video-library-blocker-ref"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{r.module_title || 'Untitled module'}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {r.section_title || 'Section'}
                        {r.slide_title ? ` · ${r.slide_title}` : ''}
                      </p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                  </Link>
                ))}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setDeleteTarget(null); setDeleteBlocked(null); }}>
                  Close
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Delete this video?</DialogTitle>
                <DialogDescription>
                  <span className="font-medium text-foreground">{deleteTarget?.filename}</span> will be permanently removed
                  from storage. This action can't be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleting}
                  data-testid="video-library-delete-confirm"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Delete
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Single row in the library table. Kept inline rather than extracted to its
 *  own file — it's not reused anywhere else and pulls a half-dozen props
 *  from the parent. */
function LibraryRow({ item, onRequestDelete }) {
  return (
    <div
      className="grid grid-cols-12 gap-3 px-4 py-3 border-b last:border-b-0 items-center hover:bg-muted/20 transition-colors"
      data-testid="video-library-row"
    >
      <div className="col-span-5 min-w-0">
        <p className="text-sm font-medium truncate" title={item.filename}>
          {item.filename}
        </p>
        {item.uploaded_by_name && (
          <p className="text-xs text-muted-foreground truncate">by {item.uploaded_by_name}</p>
        )}
      </div>
      <div className="col-span-2 text-sm tabular-nums" data-testid="video-library-size">
        {formatBytes(item.size_bytes)}
      </div>
      <div className="col-span-2 text-sm text-muted-foreground">
        {formatDate(item.created_at)}
      </div>
      <div className="col-span-2">
        {item.reference_count > 0 ? (
          <Badge variant="secondary" data-testid="video-library-usage-badge">
            In {item.reference_count} {item.reference_count === 1 ? 'slide' : 'slides'}
          </Badge>
        ) : (
          <Badge variant="outline" className="text-muted-foreground">Unused</Badge>
        )}
      </div>
      <div className="col-span-1 flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={onRequestDelete}
          aria-label={`Delete ${item.filename}`}
          data-testid="video-library-delete-btn"
        >
          <Trash2 className="w-4 h-4 text-destructive" />
        </Button>
      </div>
    </div>
  );
}
